import { CurrentUnits } from "@/libs/units/current";
import { GenericUnits } from "@/libs/units/generic";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { PercentageUnits } from "@/libs/units/percentage";
import { PowerUnits } from "@/libs/units/power";
import { VoltageUnits } from "@/libs/units/voltage";
import { EquipmentCalculatorSectionKey } from "../../calculationTypes";
import { SpeedUnits } from "@/libs/units/speed";
import type { Section, SectionDeps } from "../types";
import { ScenarioDetailSectionKey, ScenarioDetailSectionTitle } from "../types";

export const buildMotorSideCalculatorSection = ({
  scenarioCalculations,
  getHydraulicPowerUnit
}: SectionDeps): Section => ({
  id: EquipmentCalculatorSectionKey.MOTOR,
  hash: ScenarioDetailSectionKey.MOTOR,
  label: ScenarioDetailSectionTitle.MOTOR,
  routeName: "SizingParameters",
  aggregateDisplays: [
    {
      label: "Operating RPM",
      base: GenericUnits.RevolutionsPerMinute,
      model: [GenericUnits.RevolutionsPerMinute],
      mode: "read",
      value: scenarioCalculations.pumpCalculations.aggregate.operatingRPM.value
    },
    {
      label: "Motor Current",
      base: CurrentUnits.Amperes,
      model: [CurrentUnits.Amperes],
      mode: "read",
      value: scenarioCalculations.motorCalculations.motorCurrentA.value
    },
    {
      label: "Flow by Motor",
      base: SpeedUnits.FeetPerSecond,
      model: SpeedUnits.options(),
      mode: "convert",
      value:
        scenarioCalculations.motorCalculations.flowVelocityAroundMotorFTSEC
          .value
    },
    {
      label: "Total Voltage",
      base: VoltageUnits.Volts,
      model: VoltageUnits.options(),
      mode: "convert",
      value: scenarioCalculations.motorCalculations.motorVoltageV.value
    },
    {
      label: "Motor Efficiency",
      base: PercentageUnits.Ratio,
      target: PercentageUnits.Percentage,
      model: PercentageUnits.options(),
      mode: "read",
      value: scenarioCalculations.motorCalculations.motorEfficiencyRatio.value
    },
    {
      label: "Motor Power Factor",
      base: PercentageUnits.Ratio,
      model: PercentageUnits.options(),
      mode: "read",
      value:
        scenarioCalculations.motorCalculations.motorPowerFactor.value.toFixed(3)
    },
    {
      label: "Percent Motor Load",
      base: PercentageUnits.Ratio,
      target: PercentageUnits.Percentage,
      model: PercentageUnits.options(),
      mode: "read",
      value: scenarioCalculations.motorCalculations.motorLoadRatio.value
    },
    {
      label: "Startup Voltage",
      base: VoltageUnits.Volts,
      model: VoltageUnits.options(),
      mode: "convert",
      value: scenarioCalculations.surfaceDriveCalculations.startupVoltage.value
    },
    {
      label: "Motor Input HP",
      base: PowerUnits.HorsePower,
      target: getHydraulicPowerUnit().value,
      model: PowerUnits.options(),
      mode: "read",
      value: PowerUnits.Watts.toStandardUnit(
        scenarioCalculations.motorCalculations.motorInputW.value
      )
    },
    {
      label: "Motor Output HP",
      base: PowerUnits.HorsePower,
      target: getHydraulicPowerUnit().value,
      model: PowerUnits.options(),
      mode: "read",
      value: PowerUnits.Watts.toStandardUnit(
        scenarioCalculations.motorCalculations.motorOutputW.value
      )
    },
    {
      label: "Bottom of Motor Depth",
      base: LengthUnits.Feet,
      model: LargeLengthUnits.options(),
      mode: "convert",
      value: Number(
        scenarioCalculations.equipmentLengths.bottomOfMotorDepthFt.value.toFixed(
          2
        )
      )
    }
  ]
});
